import React from "react";
import styled from "styled-components";
import { Section, Container, Panel, Eyebrow, H2, Lead, CenteredHead } from "../components/ui";
import StoreButtons from "../components/StoreButtons";

const Steps = styled.ol`
  list-style: none;
  counter-reset: step;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;

  @media screen and (max-width: 48em) {
    grid-template-columns: 1fr;
    gap: 1.75rem;
    max-width: 24rem;
    margin: 0 auto;
  }
`;

const Step = styled.li`
  counter-increment: step;
  font-family: var(--fontR);

  &::before {
    content: counter(step);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    margin-bottom: 1rem;
    border-radius: 50%;
    background: var(--ink-900);
    color: var(--text-inv);
    font-family: var(--fontL);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  h3 {
    font-family: var(--fontL);
    font-size: 1.1875rem;
    font-weight: 500;
    letter-spacing: -0.01em;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9375rem;
    line-height: 1.55;
    color: var(--text-lo);
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  max-width: 900px;
  margin: 0 auto 2.5rem;

  @media screen and (max-width: 48em) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  padding: 2rem;
  border-radius: 24px;
  background: #ffffff;
  font-family: var(--fontR);

  h3 {
    font-family: var(--fontL);
    font-size: 1.1875rem;
    font-weight: 500;
    margin-bottom: 1.25rem;
  }

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  li {
    display: flex;
    align-items: baseline;
    gap: 0.625rem;
    font-size: 0.9375rem;
    line-height: 1.45;
  }

  li::before {
    content: "${(props) => (props.$never ? "✕" : "✓")}";
    flex-shrink: 0;
    font-weight: 600;
    color: ${(props) => (props.$never ? "#b3412e" : "var(--green)")};
  }
`;

const Price = styled.p`
  font-family: var(--fontL);
  font-size: clamp(3rem, 7vw, 4.5rem);
  font-weight: 600;
  letter-spacing: -0.04em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 1rem;

  small {
    display: block;
    margin-top: 0.75rem;
    font-family: var(--fontR);
    font-size: 1rem;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.4;
    color: var(--text-lo);
  }
`;

export const HowItWorks = () => (
  <Section id="demarrer" style={{ paddingTop: "5rem", paddingBottom: "5rem" }}>
    <Container>
      <CenteredHead data-reveal>
        <Eyebrow>Comment ça marche</Eyebrow>
        <H2>Trois minutes pour démarrer.</H2>
      </CenteredHead>
      <Steps>
        <Step data-reveal style={{ "--reveal-i": 0 }}>
          <h3>Tu connectes tes comptes</h3>
          <p>Connexion bancaire sécurisée par Powens, en lecture seule. Nea ne peut rien déplacer.</p>
        </Step>
        <Step data-reveal style={{ "--reveal-i": 1 }}>
          <h3>Nea fait le tri</h3>
          <p>Tes opérations arrivent classées. Tu vois tout de suite ce qu'il te reste et où part le reste.</p>
        </Step>
        <Step data-reveal style={{ "--reveal-i": 2 }}>
          <h3>Tu décides</h3>
          <p>Tes chiffres sont à jour, tes objectifs avancent, et ton copilote répond à tes questions.</p>
        </Step>
      </Steps>
    </Container>
  </Section>
);

const Pricing = () => (
  <Section id="tarif">
    <Panel data-reveal>
      <CenteredHead>
        <Eyebrow $color="#0a7a53">Offre de lancement</Eyebrow>
        <Price>
          0 €<small>toute l'app, sans abonnement</small>
        </Price>
        <Lead style={{ maxWidth: "36rem" }}>
          Pendant le lancement, Nea est 100 % gratuite. Tu l'installes, tu connectes tes comptes, tu vois si ça
          t'aide. C'est tout.
        </Lead>
      </CenteredHead>

      <Columns>
        <Column>
          <h3>Inclus, gratuitement</h3>
          <ul>
            <li>Connexion de tes comptes bancaires</li>
            <li>Budget et reste à dépenser</li>
            <li>Dépenses classées automatiquement</li>
            <li>Objectifs d'épargne</li>
            <li>Pyramide patrimoniale</li>
            <li>Copilote IA</li>
          </ul>
        </Column>
        <Column $never>
          <h3>Ce qu'on ne fera jamais</h3>
          <ul>
            <li>Revendre tes données bancaires</li>
            <li>Toucher une commission sur un produit financier</li>
            <li>Te pousser un placement pour nous arranger</li>
          </ul>
        </Column>
      </Columns>

      <StoreButtons tone="light" position="tarif" center />
    </Panel>
  </Section>
);

export default Pricing;
